import { Link } from "react-router";
import { motion } from "motion/react";
import {
  Award, Users, Shield, Target, Heart, Lightbulb, ArrowRight,
  CheckCircle, Clock, Zap, Eye, Crosshair, FileSearch,
  ClipboardCheck, Headphones, LayoutGrid, Settings, BadgeCheck,
  ChevronRight, MapPin, Sparkles
} from "lucide-react";
import { ImageWithFallback } from "./figma/ImageWithFallback";
import solaceLogo from "../../assets/Solacesmartlogo.webp";

const fadeUp: any = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const fadeLeft: any = {
  hidden: { opacity: 0, x: -50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const fadeRight: any = {
  hidden: { opacity: 0, x: 50 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } },
};
const stagger: any = { visible: { transition: { staggerChildren: 0.1 } } };
const scaleIn: any = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: "easeOut" } },
};

import { SEO } from "./SEO";

export function AboutPage() {
  return (
    <div className="relative overflow-hidden">
      <SEO
        title="About Us | Solace SMART Solutions"
        description="Learn about Solace SMART Solutions, our Japanese methodologies, vision, mission, and the dedicated team providing expert HVAC and engineering solutions in India."
      />
      {/* ── Hero ── */}
      <section className="relative bg-[#1a2d50] py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 text-sm mb-6"
          >
            <Link to="/" className="text-white/50 hover:text-white transition-colors">Home</Link>
            <ChevronRight className="w-3.5 h-3.5 text-white/30" />
            <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>About Us</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl lg:text-6xl text-white mb-5"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}
          >
            About Solace SMART Solutions
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-white/60 max-w-xl mx-auto text-lg"
            style={{ lineHeight: 1.8 }}
          >
            Bound to provide SOLACE for Professionals with SMART Methodologies to deliver the Utmost Solutions.
          </motion.p>
        </div>
      </section>

      {/* ── SOLACE · SMART · SOLUTIONS — Horizontal Timeline ── */}
      <section className="py-24 bg-[#f8f6f1]">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Name, Our Promise</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-[#1a2d50] mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              What Defines Us
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {[
              { icon: Heart, word: "SOLACE", accent: "#1a2d50", desc: "We are here to Support Professionals in Manufacturing Facilities." },
              { icon: Lightbulb, word: "SMART", accent: "#E9A319", desc: "We are a Brand with SMART Methodologies & a Goal to be Successful." },
              { icon: Target, word: "SOLUTIONS", accent: "#16a34a", desc: "We believe in comparing options, then provide multiple optimum solutions to select the best one." },
            ].map((item) => (
              <motion.div key={item.word} variants={fadeUp} className="flex">
                <div className="bg-white rounded-2xl p-8 text-center border border-[#1a2d50]/5 hover:shadow-lg transition-all duration-300 flex flex-col items-center w-full">
                  <div
                    className="w-14 h-14 rounded-xl flex items-center justify-center mb-5"
                    style={{ backgroundColor: `${item.accent}12` }}
                  >
                    <item.icon className="w-7 h-7" style={{ color: item.accent }} />
                  </div>

                  <h3 className="text-xl text-[#1a2d50] mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                    {item.word}
                  </h3>

                  <div className="w-8 h-0.5 mb-4 rounded-full" style={{ backgroundColor: item.accent }} />

                  <p className="text-[#1a2d50]/55 text-sm flex-1" style={{ lineHeight: 1.75 }}>
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Logo Story ── */}
      <section className="py-28 bg-white relative">
        {/* Subtle background pattern */}
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#f8f6f1]/50 to-transparent" />

        <div className="max-w-screen-2xl mx-auto px-6 relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid lg:grid-cols-5 gap-12 items-center">
            {/* Left – Logo (2 cols) */}
            <motion.div variants={fadeLeft} className="lg:col-span-2 flex items-center justify-center">
              <div className="relative">
                {/* Decorative rings */}
                <div className="absolute -inset-8 rounded-full border-2 border-dashed border-[#E9A319]/15 animate-[spin_40s_linear_infinite]" />
                <div className="absolute -inset-16 rounded-full border border-dashed border-[#1a2d50]/8 animate-[spin_60s_linear_infinite_reverse]" />

                <div className="relative bg-white rounded-full w-64 h-64 md:w-80 md:h-80 shadow-2xl flex items-center justify-center border-4 border-[#f8f6f1]">
                  <img
                    src={solaceLogo}
                    alt="Solace Smart Solutions Logo Explained"
                    className="w-3/4 object-contain"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right – Content (3 cols) */}
            <motion.div variants={fadeRight} className="lg:col-span-3">
              <div className="inline-flex items-center gap-2 bg-[#E9A319]/10 text-[#E9A319] rounded-full px-4 py-1.5 mb-5">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs tracking-widest uppercase" style={{ fontWeight: 700 }}>Our Identity</span>
              </div>

              <h2 className="text-3xl md:text-4xl text-[#1a2d50] mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                The Story Behind Our Logo
              </h2>

              <p className="text-[#1a2d50]/50 mb-8 max-w-xl" style={{ lineHeight: 1.8 }}>
                Every element of the Solace Smart Solutions logo carries deep meaning reflecting our values, partnership philosophy, and commitment to growth.
              </p>

              <div className="grid sm:grid-cols-2 gap-x-6 gap-y-4">
                {[
                  { text: "The shape represents the integration of two hands — the lower one belongs to Solace 'S' and the upper hand belongs to the customer.", color: "#1a2d50" },
                  { text: "The upper hand includes an arrow shape at the top, symbolizing an innovative future and search for growth.", color: "#E9A319" },
                  { text: "The bottom side arrow connects with the growth of basic engineering skills.", color: "#1a2d50" },
                  { text: "The lower hand signifies technical, integrated, value-added solutions.", color: "#E9A319" },
                  { text: "Customer upper hand strengthening by Solace.", color: "#1a2d50" },
                  { text: "The yellow color invokes the feeling of happiness, warmth, and enthusiasm.", color: "#E9A319" },
                  { text: "The blue colour creates a sense of security while showing loyalty and professionalism.", color: "#1a2d50" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 group">
                    <div className="mt-1 shrink-0 w-5 h-5 rounded-full flex items-center justify-center" style={{ backgroundColor: `${item.color}12` }}>
                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }} />
                    </div>
                    <span className="text-[#1a2d50]/70 text-sm group-hover:text-[#1a2d50] transition-colors" style={{ lineHeight: 1.6 }}>
                      {item.text}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Vision & Mission ── */}
      <section className="py-24 bg-[#1a2d50]">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Direction</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl text-white mt-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Vision & Mission
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 gap-8">
            {/* Vision */}
            <motion.div variants={fadeUp}>
              <div className="bg-white/[0.07] rounded-2xl p-8 md:p-10 border border-white/10 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 bg-[#E9A319] rounded-xl flex items-center justify-center">
                    <Eye className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    Our Vision
                  </h3>
                </div>
                <p className="text-white/65" style={{ lineHeight: 1.9 }}>
                  Solace will be India's brand with Japanese methodologies with precise engineering and a culture of{" "}
                  <span className="text-[#E9A319]" style={{ fontWeight: 600 }}>
                    Safety, Quality, Delivery, Morale & Cost
                  </span>.
                </p>
              </div>
            </motion.div>

            {/* Mission */}
            <motion.div variants={fadeUp}>
              <div className="bg-white/[0.07] rounded-2xl p-8 md:p-10 border border-white/10 h-full">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-11 h-11 bg-[#E9A319] rounded-xl flex items-center justify-center">
                    <Crosshair className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl text-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    Our Mission
                  </h3>
                </div>
                <ul className="space-y-3">
                  {[
                    "To implement Kaizen for customer delight.",
                    "To establish the Solace brand as a name of Trust & Confidence.",
                    "To integrate for the expansion of Solace with channel partners.",
                  ].map((m, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-4 h-4 text-[#E9A319] mt-1 shrink-0" />
                      <span className="text-white/65" style={{ lineHeight: 1.8 }}>{m}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Team / Leadership ── */}
      <section className="py-28 bg-[#f8f6f1] relative overflow-hidden">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <div className="grid lg:grid-cols-12 gap-12 items-center">
              {/* Text content */}
              <motion.div variants={fadeLeft} className="lg:col-span-7 order-2 lg:order-1">
                <div className="inline-flex items-center gap-2 bg-[#1a2d50]/5 text-[#1a2d50] rounded-full px-4 py-1.5 mb-5">
                  <Users className="w-4 h-4" />
                  <span className="text-xs tracking-widest uppercase" style={{ fontWeight: 700 }}>Our Leadership</span>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1a2d50] mb-8" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                  The Amazing Team Behind
                  <span className="text-[#E9A319]"> Solace</span>
                </h2>

                <div className="space-y-6">
                  {[
                    "We believe in comparing options, thus we provide multiple optimum solutions to select the best one.",
                    "The Founder Nitin Kr. Singh is a B.Tech. in Mechanical Engineering from Sharda Group of Institutes. He has worked with numerous MNCs and served a long tenure of over 12 years with the Japanese OEM Manufacturing Industry.",
                    "In 2020, he stepped forward to serve HVAC clients, bringing vast experience in Consultation, Project Management & Execution with precise engineering calculations to deliver cost and energy-efficient projects.",
                    "Seeing professional demand in the HVAC industry and the need to understand difficulties in the current scenario, the Founder's leadership team of four members was formed with a motto to provide Solace to Professionals.",
                  ].map((text, i) => (
                    <div key={i} className="flex gap-4 group">
                      <div className="shrink-0 mt-1">
                        <div className="w-8 h-8 rounded-xl bg-[#E9A319]/10 group-hover:bg-[#E9A319] flex items-center justify-center transition-colors duration-300">
                          <CheckCircle className="w-4 h-4 text-[#E9A319] group-hover:text-white transition-colors duration-300" />
                        </div>
                      </div>
                      <p className="text-[#1a2d50]/70 group-hover:text-[#1a2d50] transition-colors duration-300" style={{ lineHeight: 1.8 }}>
                        {text}
                      </p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Image */}
              <motion.div variants={fadeRight} className="lg:col-span-5 order-1 lg:order-2">
                <div className="relative">
                  {/* Decorative frame */}
                  <div className="absolute -inset-4 bg-gradient-to-br from-[#E9A319]/20 to-[#1a2d50]/20 rounded-3xl" />
                  <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                    <ImageWithFallback
                      src="https://images.unsplash.com/photo-1769685528172-b74293fdeebd?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG9mZmljZXxlbnwxfHx8fDE3NzIyODczNjZ8MA&ixlib=rb-4.1.0&q=80&w=1080"
                      alt="The Solace SMART Solutions team"
                      className="w-full aspect-[4/5] object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1a2d50]/80 via-transparent to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8">
                      <div className="flex items-center gap-6">
                        <div className="text-center">
                          <p className="text-3xl text-[#E9A319]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>12+</p>
                          <p className="text-white/60 text-xs mt-1">Years in Industry</p>
                        </div>
                        <div className="w-px h-12 bg-white/20" />
                        <div className="text-center">
                          <p className="text-3xl text-[#E9A319]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>4</p>
                          <p className="text-white/60 text-xs mt-1">Core Members</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── What & How We Perform ── */}
      <section className="py-28 bg-white relative">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-20">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>
              What & How We Perform
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Professionals to Serve India with
              <span className="block text-[#E9A319]">Japanese Methodologies</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#1a2d50]/50 max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
              We follow disciplined Japanese manufacturing principles to deliver exceptional results in every HVAC project we undertake.
            </motion.p>
          </motion.div>

          {/* Safety Check Banner */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            className="relative rounded-3xl overflow-hidden mb-14"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-[#1a2d50] via-[#1f3561] to-[#243d6a]" />
            <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23E9A319' fill-opacity='0.3'%3E%3Cpath d='M0 0h20v20H0zM20 20h20v20H20z'/%3E%3C/g%3E%3C/svg%3E\")" }} />
            <div className="relative p-10 md:p-14 flex flex-col md:flex-row items-center gap-8">
              <div className="shrink-0 w-20 h-20 bg-[#E9A319] rounded-2xl flex items-center justify-center shadow-lg shadow-[#E9A319]/30 rotate-3">
                <ClipboardCheck className="w-10 h-10 text-white" />
              </div>
              <div className="text-center md:text-left">
                <h3 className="text-2xl text-white mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                  Safety Check & Observation Closure
                </h3>
                <p className="text-white/60 max-w-2xl" style={{ lineHeight: 1.8 }}>
                  We are bound to schedule with timely updating and analysis. We focus on mitigation plans, resource management, manufacturing development, installation, customer support, and proactive troubleshooting.
                </p>
              </div>
            </div>
          </motion.div>

          {/* 4 Pillars as numbered steps */}
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Settings, title: "Project Management", num: "01", color: "from-blue-500 to-blue-700", desc: "We are bound to a schedule with timely updating and analysis. Mitigation plans, resource management, manufacturing development, installation, customer support, and proactive troubleshooting." },
              { icon: FileSearch, title: "Specification", num: "02", color: "from-[#E9A319] to-[#d4920f]", desc: "We are bound to a schedule with timely updating and analysis. Mitigation plans, resource management, manufacturing development, installation, customer support, and proactive troubleshooting." },
              { icon: LayoutGrid, title: "5S Lean Workplace", num: "03", color: "from-green-500 to-green-700", desc: "We are bound to a schedule with timely updating and analysis. Mitigation plans, resource management, manufacturing development, installation, customer support, and proactive troubleshooting." },
              { icon: Headphones, title: "Feedback & Support", num: "04", color: "from-purple-500 to-purple-700", desc: "We are bound to a schedule with timely updating and analysis. Mitigation plans, resource management, manufacturing development, installation, customer support, and proactive troubleshooting." },
            ].map((item) => (
              <motion.div key={item.title} variants={fadeUp} className="group relative">
                <div className="bg-[#f8f6f1] rounded-2xl p-7 h-full hover:bg-white hover:shadow-xl transition-all duration-500 border border-transparent hover:border-[#1a2d50]/10">
                  {/* Number watermark */}
                  <span className="absolute top-4 right-6 text-6xl text-[#1a2d50]/[0.04] select-none" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>{item.num}</span>

                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-5 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <item.icon className="w-6 h-6 text-white" />
                  </div>

                  <h3 className="text-lg text-[#1a2d50] mb-3" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>{item.title}</h3>
                  <p className="text-[#1a2d50]/50 text-sm" style={{ lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Core Values SQDMC ── */}
      <section className="py-28 bg-[#f8f6f1] relative overflow-hidden">
        {/* Decorative blobs */}
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-[#E9A319]/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-72 h-72 bg-[#1a2d50]/5 rounded-full blur-3xl" />

        <div className="max-w-6xl mx-auto px-6 relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>Our Core Values</motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              The Culture of SQDMC
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#1a2d50]/50 max-w-2xl mx-auto" style={{ lineHeight: 1.8 }}>
              Rooted in Japanese manufacturing excellence, our culture is built upon five foundational pillars that guide every project we deliver.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-5">
            {[
              { letter: "S", word: "Safety", icon: Shield, gradient: "from-red-500 to-rose-600" },
              { letter: "Q", word: "Quality", icon: BadgeCheck, gradient: "from-blue-500 to-indigo-600" },
              { letter: "D", word: "Delivery", icon: Clock, gradient: "from-green-500 to-emerald-600" },
              { letter: "M", word: "Morale", icon: Users, gradient: "from-amber-500 to-orange-600" },
              { letter: "C", word: "Cost", icon: Zap, gradient: "from-purple-500 to-violet-600" },
            ].map((v) => (
              <motion.div key={v.word} variants={fadeUp} className="group">
                <div className="bg-white rounded-2xl p-6 text-center hover:shadow-2xl transition-all duration-500 border border-[#1a2d50]/5 hover:-translate-y-2 relative overflow-hidden h-full">
                  {/* Gradient top accent */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${v.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                  <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${v.gradient} flex items-center justify-center mx-auto mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                    <v.icon className="w-7 h-7 text-white" />
                  </div>

                  <p className="text-4xl text-[#1a2d50] mb-1" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 900 }}>
                    {v.letter}
                  </p>
                  <p className="text-[#1a2d50]/60 text-sm" style={{ fontWeight: 600 }}>
                    {v.word}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── Why Choose Solace ── */}
      <section className="py-28 bg-white relative">
        <div className="max-w-screen-2xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              {/* Stats + Content */}
              <motion.div variants={fadeLeft}>
                <div className="inline-flex items-center gap-2 bg-[#E9A319]/10 text-[#E9A319] rounded-full px-4 py-1.5 mb-5">
                  <Award className="w-4 h-4" />
                  <span className="text-xs tracking-widest uppercase" style={{ fontWeight: 700 }}>Why Choose Us</span>
                </div>

                <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#1a2d50] mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                  What Sets Solace
                  <span className="text-[#E9A319]"> Apart</span>
                </h2>

                <p className="text-[#1a2d50]/50 mb-10" style={{ lineHeight: 1.8 }}>
                  With deep roots in Japanese manufacturing discipline and over a decade of hands-on experience, we bring a unique blend of precision engineering, cost optimization, and customer-centric service to every HVAC project.
                </p>

                {/* Stats grid */}
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: "12+", label: "Years in Japanese OEM Industry", colorClass: "text-[#E9A319]" },
                    { value: "100+", label: "Projects Delivered", colorClass: "text-[#1a2d50]" },
                    { value: "4", label: "Core Team Members", colorClass: "text-[#E9A319]" },
                    { value: "3", label: "States Served", colorClass: "text-[#1a2d50]" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#f8f6f1] rounded-2xl p-6 group hover:bg-[#1a2d50] transition-colors duration-500">
                      <p className={`text-3xl mb-1 ${stat.colorClass} group-hover:text-[#E9A319] transition-colors`} style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                        {stat.value}
                      </p>
                      <p className="text-[#1a2d50]/50 text-sm group-hover:text-white/60 transition-colors">{stat.label}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Image collage */}
              <motion.div variants={fadeRight} className="relative">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-4">
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <ImageWithFallback
                        src="https://images.unsplash.com/photo-1711304548487-4950020f4d62?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYW51ZmFjdHVyaW5nJTIwcGxhbnQlMjBtb2Rlcm4lMjBmYWNpbGl0eXxlbnwxfHx8fDE3NzIzNjQ3Mjl8MA&ixlib=rb-4.1.0&q=80&w=1080"
                        alt="Manufacturing facility"
                        className="w-full aspect-[3/4] object-cover"
                      />
                    </div>
                    <div className="bg-gradient-to-br from-[#E9A319] to-[#d4920f] rounded-2xl p-6 text-white shadow-lg">
                      <Award className="w-8 h-8 mb-3 text-white/80" />
                      <p className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                        Made in India
                      </p>
                      <p className="text-white/70 text-sm">Japanese Standards</p>
                    </div>
                  </div>
                  <div className="space-y-4 pt-8">
                    <div className="bg-[#1a2d50] rounded-2xl p-6 text-white shadow-lg">
                      <Sparkles className="w-8 h-8 mb-3 text-[#E9A319]" />
                      <p className="text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
                        Kaizen Culture
                      </p>
                      <p className="text-white/60 text-sm">Continuous Improvement</p>
                    </div>
                    <div className="rounded-2xl overflow-hidden shadow-lg">
                      <ImageWithFallback
                        src="https://images.unsplash.com/photo-1720036236697-018370867320?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmR1c3RyaWFsJTIwSFZBQyUyMGVuZ2luZWVyaW5nJTIwZGFya3xlbnwxfHx8fDE3NzIzNjQ3Mjh8MA&ixlib=rb-4.1.0&q=80&w=1080"
                        alt="Engineering facility"
                        className="w-full aspect-[3/4] object-cover"
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Service Locations ── */}
      <section className="py-24 bg-[#f8f6f1] relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-6 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.span variants={fadeUp} className="text-[#E9A319] text-sm tracking-widest uppercase" style={{ fontWeight: 600 }}>
              Our Presence
            </motion.span>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl text-[#1a2d50] mt-3 mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Serving Across India
            </motion.h2>
            <motion.p variants={fadeUp} className="text-[#1a2d50]/50 max-w-xl mx-auto mb-14" style={{ lineHeight: 1.8 }}>
              Our professional HVAC services reach across multiple states, delivering Japanese-standard solutions to manufacturing and commercial facilities.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
              {["Rajasthan", "Uttar Pradesh", "Gujarat"].map((state) => (
                <div
                  key={state}
                  className="group flex items-center gap-3 bg-white px-8 py-5 rounded-2xl shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 border border-[#1a2d50]/5 hover:border-[#E9A319]/30"
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E9A319]/10 group-hover:bg-[#E9A319] flex items-center justify-center transition-colors duration-300">
                    <MapPin className="w-5 h-5 text-[#E9A319] group-hover:text-white transition-colors duration-300" />
                  </div>
                  <span className="text-[#1a2d50] text-lg" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 700 }}>
                    {state}
                  </span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative py-28 overflow-hidden">
        <div className="absolute inset-0 bg-[#1a2d50]" />
        <div className="absolute inset-0 opacity-20">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1769589202187-44a3ec703bf6?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxpbmRpYSUyMHNreWxpbmUlMjBjaXR5c2NhcGUlMjBtb2Rlcm58ZW58MXx8fHwxNzcyMzY0NzI5fDA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="India cityscape"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-[#1a2d50] via-[#1a2d50]/90 to-[#1a2d50]" />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#E9A319]/10 rounded-full blur-[120px]" />

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-5xl text-white mb-6" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", fontWeight: 800 }}>
              Ready to Experience the
              <span className="text-[#E9A319]"> Solace Difference?</span>
            </motion.h2>
            <motion.p variants={fadeUp} className="text-white/50 mb-10 max-w-xl mx-auto text-lg" style={{ lineHeight: 1.8 }}>
              Partner with a team that brings precision engineering, Japanese methodologies, and a relentless commitment to your satisfaction.
            </motion.p>
            <motion.div variants={fadeUp}>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#E9A319] to-[#d4920f] text-white px-10 py-5 rounded-full hover:shadow-xl hover:shadow-[#E9A319]/20 transition-all duration-300 hover:-translate-y-0.5 group"
                style={{ fontWeight: 700 }}
              >
                Contact Us Today
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}